<template>
  <div>
    <div>
      <div class="m-1">
        <h4>
          {{ widget.param.title }}
        </h4>
      </div>
      <div>
        <cbs-collapse :trigger="isData()">
          <vue-apex-charts
            v-if="data"
            :type="chartType()"
            height="400"
            :options="chartParam().chartOptions"
            :series="querySeries()"
          />
        </cbs-collapse>
      </div>
    </div>
    <cbs-debug
      v-if="false"
      :context="this"
    />
  </div>
</template>

<script>
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'
import CbsDebug from '@/cubus/components/debug/CbsDebug.vue'
import VueApexCharts from 'vue-apexcharts'
import CbsCollapse from '@/cubus/components/collapsible/CbsCollapse.vue'
import Q from '@/cubus/services/Q'

export default {
  name: 'CbsWidgetChart',
  components: {
    CbsDebug,
    VueApexCharts,
    CbsCollapse,
  },
  props: {
    widget: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      rangePicker: ['2019-05-01', '2019-05-10'],
      chartColors: {
        column: {
          series1: '#826af9',
          series2: '#d2b0ff',
          bg: '#f8d3ff',
        },
        success: {
          shade_100: '#7eefc7',
          shade_200: '#06774f',
        },
        donut: {
          series1: '#ffe700',
          series2: '#00d4bd',
          series3: '#826bf8',
          series4: '#2b9bf4',
          series5: '#FFA1A1',
        },
        area: {
          series3: '#a4f8cd',
          series2: '#60f2ca',
          series1: '#2bdac7',
        },
      },
      query: null,
      data: null,
      // REQ-003: multi-series mode — the frontend queries the widget source itself
      seriesData: null,
      seriesCategories: null,
      targetValue: null,
    }
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      if (this.isSeriesMode()) {
        this.loadSeriesData()
      } else {
        this.loadQuery()
      }
    },
    // REQ-003: series: [{valueentity, title, color}] in the widget param switches the
    // widget to multi-series mode. The backend widget method computes a single
    // valueentity, so here the source object is queried directly and the series are
    // built client-side from its columns.
    isSeriesMode() {
      return Array.isArray(this.widget.param.series) && this.widget.param.series.length > 0
    },
    async loadSeriesData() {
      const prm = this.widget.param
      const objectid = prm.srcobject && Number(prm.srcobject.value)
      if (!objectid) return
      let delSafe = false
      try {
        const obj = await useCubus.loadObject(this, { objectid })
        delSafe = !!obj && !!obj.object && ['table', 'cube', 'catalog'].includes(obj.object.meta)
      } catch (e) {
        console.warn('widget source object load failed', e)
      }
      const qry = await useCubus.loadQuery(this, {
        keymode: 'sid',
        objectid,
        filter: delSafe ? Q.and([Q.equals('del', false)]) : null,
        limit: 1000,
      })
      if (!qry || !qry.recordset) return
      const colByEntityId = id => qry.columns.find(c => Number(c.entityid) === Number(id))
      const catCol = prm.categoryentity ? colByEntityId(prm.categoryentity.value) : null
      let { records } = qry.recordset
      if (catCol) {
        records = [...records].sort((a, b) => {
          const av = a[catCol.entitysid] ? a[catCol.entitysid].value : null
          const bv = b[catCol.entitysid] ? b[catCol.entitysid].value : null
          if (typeof av === 'number' && typeof bv === 'number') return av - bv
          return String(av).localeCompare(String(bv))
        })
      }
      const koef = Number(prm.koef) || 1
      this.seriesCategories = records.map(r => {
        if (!catCol || !r[catCol.entitysid]) return ''
        const cell = r[catCol.entitysid]
        const raw = cell.title != null && cell.title !== '' ? cell.title : cell.value
        return this.formatCategory(raw)
      })
      this.seriesData = prm.series.map(s => {
        const col = colByEntityId(s.valueentity)
        return {
          name: s.title || (col ? col.title : ''),
          data: records.map(r => {
            const cell = col ? r[col.entitysid] : null
            return cell && cell.value != null ? Number(cell.value) * koef : null
          }),
        }
      })
      this.targetValue = this.resolveTarget(qry, records, koef)
      this.data = { data: { values: [], categories: this.seriesCategories } }
    },
    // categoryformat (DD.MM / DD.MM HH:mm) applied client-side: series mode does not go
    // through the backend widget formatter
    formatCategory(raw) {
      const fmt = this.widget.param.categoryformat
      if (!fmt || typeof raw !== 'string') return raw
      const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/)
      if (!m) return raw
      const [, , mo, d, hh, mi] = m
      if (fmt.includes('HH') && hh != null) return `${d}.${mo} ${hh}:${mi}`
      return `${d}.${mo}`
    },
    // REQ-003: target: {value} — literal goal line; target: {entityid} — the value is
    // taken from the first record carrying that column (e.g. a plan field of the source)
    resolveTarget(qry, records, koef) {
      const t = this.widget.param.target
      if (!t) return null
      if (t.value != null && t.value !== '') return Number(t.value)
      if (t.entityid) {
        const col = qry.columns.find(c => Number(c.entityid) === Number(t.entityid))
        if (col) {
          const rec = records.find(r => r[col.entitysid] && r[col.entitysid].value != null)
          if (rec) return Number(rec[col.entitysid].value) * koef
        }
      }
      return null
    },
    chartParam() {
      return {
        chartOptions: {
          chart: {
            stacked: !this.isSeriesMode(),
            toolbar: {
              show: false,
            },
          },
          colors: this.chartColorList(),
          annotations: this.chartAnnotations(),
          plotOptions: {
            bar: {
              // horizontal: true,
              borderRadius: 5,
              columnWidth: '40%',
              dataLabels: {
                position: 'top',
              },
            },
          },
          dataLabels: this.chartDataLabels(),
          legend: {
            show: true,
            position: 'left',
            fontSize: '14px',
            fontFamily: 'Montserrat',
            horizontalAlign: 'left',
          },
          stroke: this.chartStroke(),
          grid: {
            xaxis: {
              lines: {
                show: true,
              },
            },
          },
          xaxis: {
            categories: this.queryCategories(),
          },
          yaxis: {
            // opposite: isRtl,
          },
          fill: this.chartFill(),
          markers: this.chartMarkers(),
        },
      }
    },
    chartOptions() {
      return this.chartParam().chartOptions
    },
    loadQuery() {
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'widget',
          param: {
            widgetid: this.widget.id,
          },
        },
      })
        .then(response => {
          // console.log('query response', response)
          // this.query = response
          if (response.data.thread) this.threadQuery(response.data.thread)
        })
        .catch(errQry => {
          console.log('query error', errQry)
        })
    },
    threadQuery(thread) {
      useJwt.query({
        query: {
          method: 'thread',
          param: { threadname: thread },
        },
      })
        .then(response => {
          if (response.data.error) {
            useCubus.toastError(response.data.error)
          } else if (response.data.thread) {
            if (response.data.thread) {
              if (response.data.thread.status === 'done') {
                this.data = response.data.thread.result.widget
              } else if (response.data.thread.status === 'error') {
                useCubus.toastError(this, response.data.thread.error)
              } else {
                this.delayQuery(thread)
              }
            } else {
              useCubus.toastError(this, 'Thread not found.')
            }
          } else {
            useCubus.toastError(this, 'Load control panel is aborted.')
          }
        })
        .catch(error => {
          console.log('thread error', error)
          useCubus.toastError(this, error.toString())
        })
    },
    delayQuery(thread) {
      setTimeout(() => this.threadQuery(thread), 500)
    },
    querySeries() {
      if (this.isSeriesMode()) {
        return this.seriesData || []
      }
      if (this.data) {
        if (this.widget.param.stackentity) {
          return this.data.data.series
        }
        return [
          {
            name: this.widget.param.title,
            data: this.data.data.values,
          },
        ]
      }
      return []
    },
    queryCategories() {
      if (this.isSeriesMode()) {
        return this.seriesCategories || []
      }
      if (this.data) {
        return this.data.data.categories
      }
      return []
    },
    isData() {
      return this.data !== null
    },
    chartType() {
      // area renders through the line engine with a fill — apexcharts' own 'area' type
      return this.widget.param.charttype || 'bar'
    },
    isLineLike() {
      return ['line', 'area'].includes(this.widget.param.charttype)
    },
    chartColorList() {
      if (!this.isSeriesMode()) return undefined
      const defaults = ['#7367f0', '#28c76f', '#ff9f43', '#ea5455', '#00cfe8']
      return this.widget.param.series.map((s, i) => s.color || defaults[i % defaults.length])
    },
    // REQ-003: target — horizontal dashed goal line
    chartAnnotations() {
      if (this.targetValue == null) return {}
      return {
        yaxis: [
          {
            y: this.targetValue,
            borderColor: '#ea5455',
            strokeDashArray: 6,
            label: {
              borderColor: '#ea5455',
              style: { color: '#fff', background: '#ea5455' },
              text: `Цель: ${this.targetValue.toLocaleString('ru-RU')}`,
            },
          },
        ],
      }
    },
    chartFill() {
      if (this.widget.param.charttype === 'area') {
        return { type: 'gradient', gradient: { opacityFrom: 0.5, opacityTo: 0.1 } }
      }
      return { opacity: 1 }
    },
    chartStroke() {
      if (this.isLineLike()) {
        return {
          show: true,
          curve: 'straight',
          width: 2,
        }
      }
      return {
        show: true,
        colors: ['transparent'],
      }
    },
    chartMarkers() {
      if (this.widget.param.charttype === 'line') {
        return {
          size: 6,
        }
      }
      if (this.widget.param.charttype === 'area') {
        return {
          size: 3,
        }
      }
      return {}
    },
    chartDataLabels() {
      if (this.isSeriesMode() && this.widget.param.charttype === 'area') {
        return { enabled: false }
      }
      if (this.isLineLike()) {
        return {
          enabled: true,
          offsetY: -10,
          background: {
            enabled: false,
          },
        }
      }
      return {
        enabled: true,
        offsetY: -20,
        background: {
          enabled: false,
        },
      }
    },
  },
}
</script>

<style scoped>

</style>
